import { OrderListItemEntity, OrderListEntity, OrderSummaryEntity } from 'orders/domain/entities/order.entity'
import type { OperationMode } from 'core/domain/operationMode'
import type { orderStates } from 'utils/constants'

interface ApiListItem {
    id: number
    state: string
    code: string
    description: string
    created_at: string
    package_count: string
    containers_count?: string | null
    chutes_count?: string | null
    warehouse_name?: string
    template_name?: string
    classification_type_key?: string
    classification_type_description?: string
    without_containerization?: boolean
}

interface ApiListResponse {
    totalItems: number
    items: ApiListItem[]
    page: number
    size: number
}

interface ApiSummaryResponse {
    active_orders: number
    processed_packages: number
    open_containers: number
    finished_orders: number
}

function buildOperationMode(item: ApiListItem): OperationMode | undefined {
    if (item.classification_type_key != null && item.classification_type_key !== '') {
        return {
            id: 0,
            code: item.classification_type_key,
            name: item.classification_type_description ?? '',
        }
    }
    return undefined
}

export class OrderAdapter {
    static toDomainListItem(item: ApiListItem): OrderListItemEntity {
        return {
            id: item.id,
            state: item.state,
            code: item.code,
            description: item.description,
            createdAt: item.created_at,
            packageCount: item.package_count,
            containersCount: item.containers_count != null ? Number(item.containers_count) : 0,
            chutesCount: item.chutes_count != null ? Number(item.chutes_count) : 0,
            warehouseName: item.warehouse_name,
            templateName: item.template_name,
            operationMode: buildOperationMode(item),
            withoutContainerization: item.without_containerization ?? false,
        }
    }

    static toDomainList(data: ApiListResponse): OrderListEntity {
        return {
            totalItems: data.totalItems,
            page: data.page,
            size: data.size,
            items: data.items.map((item) => this.toDomainListItem(item)),
        }
    }

    static toDomainSummary(data: ApiSummaryResponse): OrderSummaryEntity {
        return {
            activeOrders: data.active_orders,
            processedPackages: data.processed_packages,
            openContainers: data.open_containers,
            finishedOrders: data.finished_orders,
        }
    }

    static toViewList(items: OrderListItemEntity[]): OrderListItemView[] {
        return items.map((item) => ({
            id: item.id,
            state: item.state as orderStates,
            code: item.code,
            description: item.description,
            created_at: item.createdAt ?? undefined,
            package_count: item.packageCount,
            warehouse_name: item.warehouseName,
            operationMode: item.operationMode,
            packages: Number(item.packageCount) || 0,
            containers: item.containersCount,
            chutes: item.chutesCount,
            template: item.templateName,
            withoutContainerization: item.withoutContainerization,
        }))
    }

    static toViewSummary(entity: OrderSummaryEntity): OrderSummaryView {
        return {
            activeOrders: entity.activeOrders,
            processedPackages: entity.processedPackages,
            openContainers: entity.openContainers,
            finishedOrders: entity.finishedOrders,
        }
    }
}

export interface OrderListItemView {
    id: number
    state: orderStates
    code: string
    description: string
    created_at: string | undefined
    package_count: string
    warehouse_name?: string
    operationMode?: OperationMode
    packages?: number
    containers?: number
    chutes?: number
    template?: string
    withoutContainerization?: boolean
}

export interface OrderSummaryView {
    activeOrders: number
    processedPackages: number
    openContainers: number
    finishedOrders: number
}
